import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SectionTitle } from "@/components/Practice";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const JOTFORM_ID = process.env.REACT_APP_JOTFORM_ID;

const TOPICS = [
  { value: "general", label: "General query" },
  { value: "consulting", label: "Consulting engagement" },
  { value: "buyer", label: "I'm a buyer" },
  { value: "supplier", label: "I'm a supplier" },
];

const inputCls =
  "w-full bg-transparent border-b border-[#DFDFD3] focus:border-[#4C8C1E] outline-none py-3.5 text-base font-light text-[#16180F] placeholder:text-[#6F6F63]/60 transition-colors duration-300 rounded-none";

export default function ContactDesk() {
  const [form, setForm] = useState({ name: "", email: "", company: "", topic: "general", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/queries`, { ...form, company: form.company || null });
      toast.success("Query sent. The owner will reach out to you shortly.");
      setForm({ name: "", email: "", company: "", topic: "general", message: "" });
    } catch (err) {
      console.error("Query submit failed:", err?.response?.status, err?.response?.data);
      toast.error(
        err?.response?.status === 422
          ? "Please double-check your details and try again."
          : "Could not send your query. Please try again."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 md:py-28">
      <SectionTitle kicker="Reach the Owner" title="Drop us a line." />
      <Reveal delay={0.15}>
        <p className="mt-6 text-center text-[11px] tracking-[0.3em] uppercase text-[#6F6F63]">
          Strategy · Transformation · Finance · Advisory
        </p>
        <p className="mt-2 text-center text-[11px] tracking-[0.3em] uppercase text-[#6F6F63]">
          Mississauga, Ontario L5W 1X3
        </p>
      </Reveal>

      <div className="max-w-xl mx-auto px-6 mt-14">
        {JOTFORM_ID ? (
          <Reveal>
            <iframe
              title="eProcure query form"
              data-testid="jotform-embed"
              src={`https://form.jotform.com/${JOTFORM_ID}`}
              className="w-full min-h-[640px] border-0 bg-transparent"
              allowTransparency="true"
              allow="geolocation; microphone; camera; fullscreen"
            />
          </Reveal>
        ) : (
          <Reveal delay={0.2}>
            <form onSubmit={submit} data-testid="contact-form" className="space-y-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                <input id="q-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Name *" aria-label="Name" className={inputCls} />
                <input id="q-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="Email *" aria-label="Email" className={inputCls} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                <input id="q-company" data-testid="contact-company-input" value={form.company} onChange={set("company")} placeholder="Company" aria-label="Company" className={inputCls} />
                <select id="q-topic" data-testid="contact-topic-select" value={form.topic} onChange={set("topic")} aria-label="Topic" className={`${inputCls} bg-white`}>
                  {TOPICS.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <textarea id="q-message" data-testid="contact-message-input" required rows={5} value={form.message} onChange={set("message")} placeholder="Your query *" aria-label="Your query" className={`${inputCls} resize-none`} />
              <div className="text-center pt-2">
                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={sending}
                  className="group inline-flex items-center gap-3 bg-[#16180F] text-white text-[11px] font-semibold tracking-[0.3em] uppercase px-10 py-4 hover:bg-[#4C8C1E] disabled:opacity-50 transition-colors duration-300"
                >
                  {sending ? "Sending…" : "Send"}
                  <Send className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-300" />
                </button>
              </div>
            </form>
          </Reveal>
        )}
      </div>
    </section>
  );
}
